/**
 * Carousel — the vertical feed.
 *
 * Design note: CSS scroll-snap cannot coexist with games that consume touch input (every
 * touch would scroll the feed), so the track is a compositor-only transform driven by a
 * gesture arbiter:
 *   - EVERY pointer event belongs to the card. There is no gesture arbitration: navigation is
 *     the BLITZ button and the back chevron in the bottom bar, and nothing else. A feed of
 *     interactive content cannot also claim gestures from it -- whichever heuristic decides
 *     the split is wrong some of the time, and a card in a sandboxed iframe never surfaces
 *     the gesture at all. The bar lives in the parent DOM, so it works over every card class
 *     equally.
 *   - pointerdown is forwarded to the active game immediately (latency matters), and every
 *     pointer event after it follows; nothing is ever taken back for navigation.
 * Snapping is a 320ms transform transition (no layout, no main-thread animation loop).
 *
 * Slot policy (memory budget 150MB): live GameHosts exist only for N-1, N and N+1.
 *   N     running            N+1 compiled + tick 0 rendered, paused
 *   N-1   paused (kept)      N+2 assets prefetched by the service worker
 * Moving to N+1 destroys the host at N-1 (it becomes N-2): worker terminated, canvases freed.
 */
import { For, Show, batch, createEffect, createMemo, createSignal, on, onCleanup, onMount } from 'solid-js';
import { createStore } from 'solid-js/store';
import type { GameMeta, HostState } from '../lib/protocol';
import { GameHost } from '../lib/GameHost';
import { WebCardHost, sourceOf } from '../lib/WebCardHost';

/** A slot holds whichever host presents its card. Both expose the same lifecycle. */
export type SlotHost = GameHost | WebCardHost;
import { InputKind } from '../lib/InputPipeline';
import { prefetch } from '../lib/ServiceWorker';
import type { LiveStats } from '../telemetry/TelemetryTypes';
import type { TelemetryTransport } from '../telemetry/TelemetryTransport';
import { GameViewer } from './GameViewer';

export const IDLE_TIMEOUT_MS = 20_000;
const MAX_DPR = 2;
/**
 * Height of the meta strip (title, handle, verbs) under each card's surface. The chrome must
 * never paint over the card — a linked site's own header and controls have to stay visible —
 * so the viewport sits between the top and bottom bars and the strip is subtracted here, so
 * a wasm game is sized to the surface it actually gets. Mirrored by .overlay in index.css.
 */
export const META_STRIP_H = 56;

export interface CarouselApi {
  index(): number;
  count(): number;
  goTo(i: number, via?: 'swipe' | 'key' | 'wheel' | 'api'): void;
  next(): void;
  prev(): void;
  hosts(): SlotHost[];
  activeHost(): SlotHost | null;
  insertGame(meta: GameMeta): void;
  /** A card left the feed (its maker took it down): drop it and keep the cursor on the same card. */
  removeGame(id: string): void;
  prefetchNext(): Promise<unknown>;
  logs(): string[];
  setForcePostMessage(v: boolean): void;
  forcePostMessage(): boolean;
}

export interface CarouselProps {
  games: GameMeta[];
  /** Start on this game id (deep link: ?game=<id>). */
  initialGameId?: string | null;
  transport: TelemetryTransport;
  userId: string;
  debug: boolean;
  onStats: (stats: LiveStats | null) => void;
  onIndex: (index: number) => void;
  onInsert: (meta: GameMeta, at: number) => void;
  /** Drop a card from the list (the carousel has already settled its own cursor and session). */
  onRemove: (id: string) => void;
  register: (api: CarouselApi) => void;
  toast: (message: string) => void;
  /** Open the comments on a card. */
  onComments: (meta: GameMeta) => void;
  /** Live comment count per card id (registry value, updated as people post). */
  commentCounts: Record<string, number>;
  /** Whether this person has liked a card (kept on their profile). */
  liked: (id: string) => boolean;
  /** The heart was tapped on a card. */
  onLike: (meta: GameMeta, liked: boolean) => void;
  /** The end card's "Link something": open the + sheet. */
  onLink: () => void;
  /**
   * BLITZ on the end card, just before the feed starts over from the top. The app uses it to
   * swap in whatever the server has added since — the loop is also the refresh.
   */
  onLoop?: () => void;
  /** A host reached 'running'. The app keeps the first such moment of the visit as boot_ms. */
  onFirstRunning?: () => void;
}

interface SlotView {
  host: SlotHost;
  state: HostState;
}

interface Drag {
  id: number;
  startX: number;
  startY: number;
  startT: number;
  lastY: number;
  lastT: number;
}

const KEY_CODES: Record<string, number> = {
  ' ': 32,
  ArrowLeft: 37,
  ArrowUp: 38,
  ArrowRight: 39,
  ArrowDown: 40,
  Enter: 13,
  Escape: 27,
};

function keyCode(e: KeyboardEvent): number {
  if (e.key.length === 1) return e.key.toUpperCase().charCodeAt(0);
  return KEY_CODES[e.key] ?? 0;
}

export function Carousel(props: CarouselProps) {
  let viewport!: HTMLDivElement;
  const initial = Math.max(0, props.games.findIndex((g) => g.id === props.initialGameId));
  const [index, setIndex] = createSignal(initial);
  const [offset, setOffset] = createSignal(0);
  const [snapping, setSnapping] = createSignal(false);
  const [size, setSize] = createSignal({ w: 375, h: 667 });
  const [slots, setSlots] = createStore<Record<string, SlotView | undefined>>({});
  const [forcePM, setForcePM] = createSignal(false);
  const [measured, setMeasured] = createSignal(false);
  /** This visit's record: every card that has run on screen since the page loaded, for the end card's count. */
  const [played, setPlayed] = createSignal<ReadonlySet<string>>(new Set());
  /** Cards in the deck now that had a session this visit (a card since removed does not count). */
  const playedCount = () => games().filter((g) => played().has(g.id)).length;
  const hosts = new Map<string, SlotHost>();
  const logs: string[] = [];
  let drag: Drag | null = null;

  const dpr = () => Math.min(MAX_DPR, window.devicePixelRatio || 1);
  const games = () => props.games;
  /**
   * The slide after the last card is the end card: not a GameMeta, no host, drawn by this
   * component. Index `games().length` is it. BLITZ there starts over — the feed never ends
   * on a dead button — and the arrow keys and wheel clamp on it, so only BLITZ wraps.
   */
  const endIndex = () => games().length;
  const atEnd = () => index() >= endIndex();
  const activeGame = createMemo(() => games()[index()] ?? null);
  const activeHost = () => {
    const g = activeGame();
    return g ? (hosts.get(g.id) ?? null) : null;
  };

  function pushLog(line: string) {
    logs.push(`${new Date().toLocaleTimeString()} ${line}`);
    if (logs.length > 200) logs.shift();
  }

  function createHost(meta: GameMeta): SlotHost {
    const { w, h } = size();
    const host: SlotHost =
      meta.kind === 'web'
        ? new WebCardHost(meta, { userId: props.userId, debug: props.debug })
        : new GameHost(meta, {
            width: w,
            height: h - META_STRIP_H,
            dpr: dpr(),
            userId: props.userId,
            debug: props.debug,
            forcePostMessage: forcePM(),
          });
    host.on('state', (s) => {
      if (slots[meta.id]?.host === host) setSlots(meta.id, 'state', s);
      if (s === 'running') {
        props.onFirstRunning?.();
        if (!played().has(meta.id)) setPlayed((p) => new Set(p).add(meta.id));
      }
      pushLog(`${meta.id}: ${s}`);
      // N+1 finished compiling while we were already on it (fast swipe) — start it.
      if (s === 'ready' && activeGame()?.id === meta.id && document.visibilityState === 'visible') host.resume();
    });
    host.on('ready', (info) => {
      pushLog(
        `${meta.id}: compiled ${info.compileMs}ms, init ${info.initMs}ms, first frame ${info.firstFrameMs}ms, ${info.gl}, input ${info.inputMode}` +
          (info.imports.missing.length ? `, MISSING IMPORTS: ${info.imports.missing.join(' ')}` : ''),
      );
      if (info.imports.missing.length) props.toast(`${meta.title}: ${info.imports.missing.length} unsupported import(s) — see dev toolbar`);
    });
    host.on('telemetry', (batch) => props.transport.send(batch));
    host.on('stats', (stats) => {
      if (activeGame()?.id === meta.id) props.onStats(stats);
    });
    host.on('haptic', (ms) => navigator.vibrate?.(ms));
    host.on('error', (message, phase) => {
      pushLog(`${meta.id}: CRASH in ${phase}: ${message}`);
      props.toast(`${meta.title} crashed (${phase})`);
    });
    host.on('log', (message) => pushLog(`${meta.id}: ${message}`));
    hosts.set(meta.id, host);
    setSlots(meta.id, { host, state: host.state });
    return host;
  }

  function destroyHost(id: string) {
    const host = hosts.get(id);
    if (!host) return;
    hosts.delete(id);
    host.destroy();
    setSlots(id, undefined);
  }

  /** Reconcile live hosts with the N-1 / N / N+1 window around the active index. */
  function syncWindow() {
    const list = games();
    const i = index();
    if (!list.length) return;
    const wanted = new Set<string>();
    for (const k of [i, i + 1, i - 1]) {
      if (k >= 0 && k < list.length) wanted.add(list[k].id);
    }
    for (const id of [...hosts.keys()]) {
      if (!wanted.has(id)) destroyHost(id); // N-2 (or further) → garbage collected
    }
    for (const k of [i, i + 1, i - 1]) {
      const meta = list[k];
      if (meta && !hosts.has(meta.id)) createHost(meta); // GameViewer mounts canvases + load()
    }
    const activeId = list[i]?.id; // undefined on the end card
    for (const [id, host] of hosts) {
      if (id === activeId) {
        if ((host.state === 'ready' || host.state === 'paused') && document.visibilityState === 'visible') host.resume();
        continue;
      }
      // Behind the cursor is asleep, ahead of it is warm. leave() sleeps the card being left,
      // but a jump of two (a link that is already in the feed) turns the preloaded N+1 into
      // N-1 without passing through leave(); this catches it. sleep() is idempotent.
      const at = list.findIndex((g) => g.id === id);
      if (at >= 0 && at < i) host.sleep();
      else if (host.state === 'running') host.pause('swipe');
      // The N+1 you just came back from was slept on the way out: load it again, no session,
      // so BLITZ onto it is as warm as the slot policy promises.
      else if (at === i + 1 && host instanceof WebCardHost && host.asleep) host.warm();
    }
    // N+2: warm the cache while N plays and N+1 sits compiled.
    const n2 = list[i + 2];
    if (n2 && !n2.local) {
      void prefetch([n2.wasm_url, n2.data_url, n2.thumbnail_url].filter((u): u is string => !!u)).then((r) => {
        if (r) pushLog(`prefetch N+2 (${n2.id}): ${r.ok.length} ok, ${r.failed.length} failed`);
      });
    }
  }

  // Hosts are sized from the measured viewport, so wait for the first measurement.
  createEffect(on([index, () => props.games.length, measured], () => measured() && syncWindow()));
  createEffect(() => props.onIndex(index()));

  /** If focus sits in a slide that is not the active one, move it to the viewport (no scroll). */
  function reclaimFocus() {
    const el = document.activeElement;
    if (el instanceof HTMLElement && el.closest('.slide:not([data-active]), .end-slide:not([data-active])')) {
      viewport.focus({ preventScroll: true });
      if (document.activeElement === el) el.blur();
      viewport.scrollTop = 0;
      viewport.scrollLeft = 0;
    }
  }
  createEffect(on(index, () => queueMicrotask(reclaimFocus), { defer: true }));

  /**
   * The card at `current` is leaving the screen: close its session, then put it to sleep. For
   * a wasm host sleep is the pause it already gets; for a web card it unloads the page, because
   * a document in a cross-origin frame cannot be paused and would otherwise keep playing its
   * audio behind the next card. The host itself stays as the kept N-1 slot.
   */
  function leave(current: number, forward: boolean) {
    const meta = games()[current];
    const leaving = meta ? hosts.get(meta.id) : undefined;
    // A web card's exit() is a no-op without an open session, so it is called in every state:
    // a wake reload opens the session before the page arrives, and a bounce while it is still
    // 'loading' (or after it failed) is a session the ranker must see.
    const sessionState = leaving instanceof WebCardHost || leaving?.state === 'running' || leaving?.state === 'paused';
    if (leaving && sessionState) {
      leaving.exit(forward ? 'SWIPE_UP' : 'SWIPE_DOWN');
    }
    leaving?.sleep();
    props.onStats(null);
  }

  function goTo(target: number, via: 'swipe' | 'key' | 'wheel' | 'api' = 'api') {
    const clamped = Math.max(0, Math.min(endIndex(), target)); // the end card is a stop
    const current = index();
    setSnapping(true);
    setOffset(0);
    if (clamped === current) return;
    leave(current, clamped > current);
    pushLog(`goTo ${clamped} via ${via}`);
    setIndex(clamped);
  }

  /**
   * Back to the top from the end card. A jump, not a slide: the track would otherwise travel
   * the whole deck in 320 ms. onLoop runs in the same batch as the index change, so a feed
   * the app swaps in lands with card 0 already active and the window built once.
   */
  function restart() {
    const current = index();
    pushLog('loop: back to the top');
    leave(current, true);
    setSnapping(false);
    setOffset(0);
    batch(() => {
      props.onLoop?.();
      setIndex(0);
    });
    requestAnimationFrame(() => setSnapping(true));
  }

  // ---------------------------------------------------------------- gestures
  const isChrome = (t: EventTarget | null) => t instanceof Element && !!t.closest('[data-ui]');
  const epoch = (e: Event) => performance.timeOrigin + e.timeStamp;

  function forward(kind: number, e: PointerEvent) {
    const host = activeHost();
    if (!host) return;
    const rect = viewport.getBoundingClientRect();
    host.input(kind, e.pointerId, e.clientX - rect.left, e.clientY - rect.top, epoch(e));
  }

  function onPointerDown(e: PointerEvent) {
    if (isChrome(e.target)) return;
    const host = activeHost();
    if (host && (host.state === 'paused' || host.state === 'ready')) {
      host.resume(); // tap-to-resume after an idle timeout
    }
    if (drag) {
      forward(InputKind.PointerDown, e); // secondary finger: pure game input
      return;
    }
    drag = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startT: e.timeStamp,
      lastY: e.clientY,
      lastT: e.timeStamp,
    };
    try {
      viewport.setPointerCapture(e.pointerId);
    } catch {
      /* not all targets support capture */
    }
    setSnapping(false);
    forward(InputKind.PointerDown, e);
  }

  function onPointerMove(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.id) {
      if (drag || !isChrome(e.target)) forward(InputKind.PointerMove, e);
      return;
    }
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    drag.lastY = e.clientY;
    drag.lastT = e.timeStamp;

    // No arbitration: gestures belong to the card, always. Navigation is the BLITZ button.
    void dx;
    void dy;
    forward(InputKind.PointerMove, e);
  }

  function endDrag(e: PointerEvent, cancelled: boolean) {
    if (!drag || e.pointerId !== drag.id) {
      if (drag || !isChrome(e.target)) forward(cancelled ? InputKind.PointerCancel : InputKind.PointerUp, e);
      return;
    }
    drag = null;
    forward(cancelled ? InputKind.PointerCancel : InputKind.PointerUp, e);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === 'j') {
      e.preventDefault();
      goTo(index() + 1, 'key');
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'k') {
      e.preventDefault();
      goTo(index() - 1, 'key');
      return;
    }
    const code = keyCode(e);
    if (code) {
      if (e.key === ' ') e.preventDefault();
      activeHost()?.input(InputKind.KeyDown, code, 0, 0, epoch(e));
    }
  }
  function onKeyUp(e: KeyboardEvent) {
    const code = keyCode(e);
    if (code) activeHost()?.input(InputKind.KeyUp, code, 0, 0, epoch(e));
  }

  let wheelAccum = 0;
  let wheelCooldownUntil = 0;
  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const now = performance.now();
    if (now < wheelCooldownUntil) return;
    wheelAccum += e.deltaY;
    if (Math.abs(wheelAccum) > 80) {
      goTo(index() + (wheelAccum > 0 ? 1 : -1), 'wheel');
      wheelAccum = 0;
      wheelCooldownUntil = now + 450;
    }
  }

  function onGyro(e: DeviceOrientationEvent) {
    const host = activeHost();
    if (host && e.beta != null && e.gamma != null) host.gyro(e.beta, e.gamma, e.alpha ?? 0);
  }

  function onVisibility() {
    const host = activeHost();
    if (!host) return;
    if (document.visibilityState === 'hidden') {
      if (host.state === 'running') host.exit('TAB_HIDDEN');
      props.transport.flush(true);
    } else if (host.state === 'paused' || host.state === 'ready') {
      host.resume();
    }
  }

  onMount(() => {
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      const w = Math.round(r.width);
      const h = Math.round(r.height);
      if (w && h && (w !== size().w || h !== size().h)) {
        setSize({ w, h });
        for (const host of hosts.values()) host.resize(w, h - META_STRIP_H, dpr());
      }
    });
    ro.observe(viewport);
    const rect = viewport.getBoundingClientRect();
    if (rect.width && rect.height) setSize({ w: Math.round(rect.width), h: Math.round(rect.height) });
    setMeasured(true);
    // Belt and braces for the CSS overflow:clip on .viewport: the only thing that moves the
    // track is the transform. If any engine still scrolls this box (a preloaded frame focusing
    // an input, a scrollIntoView from a card), put it back before a frame is painted.
    const unscroll = () => {
      if (viewport.scrollTop || viewport.scrollLeft) {
        viewport.scrollTop = 0;
        viewport.scrollLeft = 0;
      }
    };
    viewport.addEventListener('scroll', unscroll, { passive: true });
    document.addEventListener('scroll', unscroll, { passive: true, capture: true });
    // inert stops US focusing an off-screen slide, but a page inside a preloaded frame can
    // still focus its own input, which makes that frame the document's active element — and
    // while focus sits in a cross-origin frame this document sees no key events at all. Two
    // ways in: the window blurs when focus enters a frame; and a card that held focus while
    // active keeps it after BLITZ moves on. Both hand focus to the viewport itself.
    window.addEventListener('blur', () => setTimeout(reclaimFocus, 0));

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', (e) => endDrag(e, false));
    viewport.addEventListener('pointercancel', (e) => endDrag(e, true));
    viewport.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('deviceorientation', onGyro);
    document.addEventListener('visibilitychange', onVisibility);

    // ?idle=<ms> shortens the idle timeout (used by the e2e suite).
    // An external site has no timer. Its input never reaches us and it never posts touches, so
    // the sweep would end every one of its sessions at the timeout and the read would be blind
    // on card 0. Its session ends only on BLITZ, back, the tab hiding, the slot going, or a
    // wake reload that failed (CRASH) — never IDLE_TIMEOUT.
    const idleMs = Number(new URLSearchParams(location.search).get('idle')) || IDLE_TIMEOUT_MS;
    const idleTimer = window.setInterval(() => {
      const host = activeHost();
      if (!host || host.state !== 'running' || sourceOf(host.meta) === 'external') return;
      if (performance.now() - host.lastInputAt > idleMs) {
        host.exit('IDLE_TIMEOUT');
        pushLog(`${host.meta.id}: idle timeout`);
      }
    }, 1000);

    onCleanup(() => {
      ro.disconnect();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('deviceorientation', onGyro);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(idleTimer);
      for (const id of [...hosts.keys()]) destroyHost(id);
    });

    props.register({
      index,
      count: () => games().length,
      goTo,
      next: () => (atEnd() ? restart() : goTo(index() + 1)),
      prev: () => goTo(index() - 1),
      hosts: () => [...hosts.values()],
      activeHost,
      insertGame: (meta) => {
        const at = Math.min(games().length, index() + 1);
        props.onInsert(meta, at);
        queueMicrotask(() => goTo(at, 'api'));
      },
      removeGame: (id) => {
        const at = games().findIndex((g) => g.id === id);
        if (at < 0) {
          props.onRemove(id);
          return;
        }
        const cur = index();
        // The list and the cursor move together, in one batch, so the same card stays on screen
        // when one before it goes; when the removed card IS on screen, its session closes and
        // the next card comes up in its place (or the end card, if it was last).
        batch(() => {
          if (at === cur) leave(cur, true);
          props.onRemove(id);
          if (at < cur) setIndex(cur - 1);
          else if (at === cur) setIndex(Math.min(cur, games().length));
        });
        pushLog(`removed ${id} (was ${at}); now at ${index()}`);
        props.onIndex(index()); // the address bar follows even when the number did not change
      },
      prefetchNext: () => {
        const n2 = games()[index() + 2];
        return n2 ? prefetch([n2.wasm_url, n2.thumbnail_url]) : Promise.resolve(null);
      },
      logs: () => logs,
      setForcePostMessage: setForcePM,
      forcePostMessage: forcePM,
    });
  });

  const transform = () => `translate3d(0, ${-(index() * size().h) + offset()}px, 0)`;

  return (
    <div class="viewport" ref={viewport} role="feed" aria-label="Game feed" tabindex="-1">
      <div class="track" classList={{ snapping: snapping() }} style={{ transform: transform() }}>
        <For each={props.games}>
          {(game, i) => (
            <section
              class="slide"
              style={{ height: `${size().h}px`, '--slide-accent': game.accent ?? 'var(--accent)' }}
              aria-roledescription="game"
              aria-label={game.title}
              data-game-id={game.id}
              data-active={i() === index() ? 'true' : undefined}
              // Off-screen slides (the preloaded N+1, the kept N-1) cannot take focus: a page in
              // a preloaded frame focusing its own input must not become the document's focus.
              inert={i() !== index() ? true : undefined}
            >
              <Show
                when={slots[game.id]?.host}
                keyed
                fallback={
                  <div class="slide-thumb">
                    <img src={game.thumbnail_url} alt="" loading="lazy" />
                    <div class="thumb-title">{game.title}</div>
                  </div>
                }
              >
                {(host) => (
                  <GameViewer
                    host={host}
                    meta={game}
                    state={slots[game.id]?.state ?? host.state}
                    active={i() === index()}
                    onResume={() => host.resume()}
                    toast={props.toast}
                    userId={props.userId}
                    onComments={props.onComments}
                    commentCount={props.commentCounts[game.id] ?? game.comment_count ?? 0}
                    liked={props.liked(game.id)}
                    onLike={props.onLike}
                  />
                )}
              </Show>
            </section>
          )}
        </For>
        {/* The end of the deck: honest about how much there is, and the door to adding more.
            The button below never dims — BLITZ here goes back to the top. */}
        <section
          class="end-slide"
          style={{ height: `${size().h}px` }}
          aria-label="End of the feed"
          data-testid="end-slide"
          data-active={atEnd() ? 'true' : undefined}
          inert={atEnd() ? undefined : true}
        >
          <div class="end-card" data-ui>
            <p class="kicker">That's everything, for now</p>
            {/* What this visit actually saw, not a claim that you saw everything: BLITZ skims
                past a loading card, and a share link lands mid-deck. */}
            <h2 data-testid="end-count">
              You've seen {playedCount()} of {games().length} {games().length === 1 ? 'card' : 'cards'}.
            </h2>
            <p class="sub">New cards land here as people link and upload them. Yours could be next.</p>
            <div class="actions">
              <button type="button" class="btn primary" onClick={() => props.onLink()} data-testid="end-link">
                Link something
              </button>
              <button type="button" class="btn" onClick={restart} data-testid="end-again">
                Back to the top
              </button>
            </div>
            <p class="hint">BLITZ starts over &uarr;</p>
          </div>
        </section>
      </div>
      {/* No arrows here. Forward is the BLITZ button and back is the chevron beside it, both in
          the bottom bar (TelemetryDisplay): the one control content cannot capture, on every
          device. The desktop-only arrows that used to sit here were invisible on every phone. */}
    </div>
  );
}
